import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Row,
  Col,
  Pagination,
  Collapse,
  Form,
  Button,
  Input,
  message,
  Select,
  Table,
  DatePicker,
  Tabs,
  InputNumber,
  Switch,
  Tooltip,
  Modal,
  Typography,
} from "antd";
import {
  InfoCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import PageHeader from "../Layout/PageHeader";
import AnnouncementFilters from "./AnnouncementFilters";
import { CaretRightOutlined } from "@ant-design/icons";
import { formatDate, formatDateTime } from "../../helpers/date";
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const now = new Date()

const Announcement = () => {
  const { permissions } = usePermissions();
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [announcements, setAnnouncements] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState({
    index: false,
    create: false,
    update: false,
  });
  const [form] = Form.useForm();
  const quillRef = useRef(null)

  // Quill modules configuration for toolbar
  const quillModules = {
    toolbar: {
      container: [
        [{ header: [1, 2, false] }],
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "image"],
      ],
      handlers: {
        image: imageHandler,
      },
    },
  };

  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "list",
    "bullet",
    "link",
    "image",
  ];

  const fetchAnnouncements = useCallback(async () => {
    setLoading((prev) => ({ ...prev, index: true }));
    try {
      const params = {
        ...filters,
        type: 'dashboard',
        limit: pagination.limit,
        page: pagination.currentPage,
      };
      const res = await axios.get("/api/announcements", { params });
      setAnnouncements(res.data.data);
      setPagination(res.data.pagination);
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, index: false }));
    }
  }, [pagination.currentPage, pagination.limit, filters]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [filters]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const createHandler = async (values) => {
    setLoading((prev) => ({ ...prev, create: true }));
    const languages = ["en", "ms", "zh"];

    // Common fields
    const commonData = {
      type: values.type,
      startDate: values.dateRange ? values.dateRange[0].startOf('day').toISOString() : null,
      endDate: values.dateRange ? values.dateRange[1].endOf('day').toISOString() : null,
      doNotShowDays: values.doNotShowDays,
    };

    const missingLanguages = [];
    for (const lang of languages) {
      const titleKey = `title_${lang}`;
      const textKey = `text_${lang}`;

      if (!values[titleKey] || !values[textKey]) {
        const langName = lang === "en" ? "English" : lang === "ms" ? "Malay" : "Chinese";
        missingLanguages.push(langName);
      }
    }

    if (missingLanguages.length > 0) {
      message.error(`All 3 languages are required. Missing: ${missingLanguages.join(", ")}`);
      setLoading((prev) => ({ ...prev, create: false }));
      return;
    }

    const announcementsToCreate = [];
    for (const lang of languages) {
      const titleKey = `title_${lang}`;
      const textKey = `text_${lang}`;

      announcementsToCreate.push({
        ...commonData,
        title: values[titleKey],
        text: values[textKey],
        lang: lang,
      });
    }

    try {
      await axios.post("/api/announcements/bulk", {
        announcements: announcementsToCreate,
      });
      message.success(`Successfully created ${announcementsToCreate.length} announcement(s).`);
      window.location.reload(); 
      fetchAnnouncements();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    }

    setLoading((prev) => ({ ...prev, create: false }));
  };

  const updateHandler = async (row, changes) => {
    setLoading((prev) => ({ ...prev, update: true }));
    try {
      await axios.put(`/api/announcements/${row.objectId}`, changes);
      message.success("Announcement updated");
      fetchAnnouncements();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading((prev) => ({ ...prev, update: false }));
    }
  };

  function imageHandler() {
    if (!quillRef.current) return

    const editor = quillRef.current.getEditor()
    const range = editor.getSelection()
    const value = prompt("Please enter the image URL")

    if (value && range) {
      editor.insertEmbed(range.index, "image", value, "user")
    }
  }

  const columns = [
    {
      title: "Label",
      dataIndex: "title",
      width: 400,
      render: (_, record) => {
        return permissions.includes(PERMISSIONS.UPDATE_ANNOUCEMENT) ? (
          <Input
            key={record.objectId}
            defaultValue={record.title}
            onBlur={(e) => {
              if (e.target.value !== record.title) {
                updateHandler(record, { title: e.target.value });
              }
            }}
          />
        ) : (
          record.title
        );
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      width: 100,
    },
    {
      title: "Language",
      dataIndex: "lang",
      width: 80,
      render: (lang) => (lang ? lang.toUpperCase() : "-"),
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      width: 150,
      render: (date) => {
        if (!date) {
          return "-"
        }

        const startDate = date.iso || date
        const isStartDateActive = now > new Date(startDate)

        return isStartDateActive 
          ? <Typography.Text type="success">{formatDate(startDate)}</Typography.Text>
          : <Typography.Text type="danger">{formatDate(startDate)}</Typography.Text>
      },
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      width: 150,
      render: (date) => {
        if (!date) {
          return "-"
        }

        const endDate = date.iso || date
        const isEndDateActive = now < new Date(endDate)

        return isEndDateActive 
          ? <Typography.Text type="success">{formatDate(endDate)}</Typography.Text>
          : <Typography.Text type="danger">{formatDate(endDate)}</Typography.Text>
      },
    },
    {
      title: (
        <span>
          Active{" "}
          <Tooltip title={<span>Announcement will only be shown if BOTH conditions met:<br/> i. Active <br />ii. Within Date Range</span>}>
            <InfoCircleOutlined />
          </Tooltip>
        </span>
      ),
      dataIndex: "isAnnouncement",
      width: 100,
      render: (_, record) =>
        permissions.includes(PERMISSIONS.UPDATE_ANNOUCEMENT) ? (
          <Switch
            checked={record.isAnnouncement}
            onChange={(checked) => updateHandler(record, { isAnnouncement: checked })}
          />
        ) : record.isAnnouncement ? (
          "Yes"
        ) : (
          "No"
        ),
    },
    {
      title: "Action",
      dataIndex: "text",
      ellipsis: true,
      render: (_, record) =>
        <Tooltip title="Click to edit HTML in rich text editor.">
          <Button
            size="small"
            onClick={() => {
              let content = record.text;
              Modal.info({
                title: "Edit Content",
                width: 800,
                content: (
                  <div style={{ height: "400px" }}>
                    <ReactQuill
                      ref={quillRef}
                      theme="snow"
                      defaultValue={content}
                      modules={quillModules}
                      formats={quillFormats}
                      onChange={(value) => {
                        content = value;
                      }}
                      style={{ height: "300px" }}
                    />
                    <div style={{ marginTop: "50px", float: "right" }}>
                      <Button onClick={() => Modal.destroyAll()} style={{ marginRight: 8 }}>
                        Close
                      </Button>
                      <Button
                        disabled={!permissions.includes(PERMISSIONS.UPDATE_ANNOUCEMENT)}
                        type="primary"
                        onClick={() => {
                          updateHandler(record, { text: content });
                          Modal.destroyAll();
                        }}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                ),
                okButtonProps: { style: { display: "none" } },
              });
            }}
          >
            {permissions.includes(PERMISSIONS.UPDATE_ANNOUCEMENT) ? "Edit Content" : "View Content"}
          </Button>
        </Tooltip>
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      width: 150,
      render: (date) => (date ? formatDateTime(date.iso || date) : "-"),
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      width: 150,
      render: (date) => (date ? formatDateTime(date.iso || date) : "-"),
    },
  ];

  return (
    <div>
      <PageHeader title="Announcement" childRoutes={[{ path: "/announcement", breadcrumbName: "Announcement" }]} />

      {permissions.includes(PERMISSIONS.CREATE_ANNOUCEMENT) && (
        <Collapse expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}>
          <Collapse.Panel header="Create New Announcement" key="create-announcement">
            <Form form={form} layout="vertical" onFinish={createHandler}>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="type" label="Type" rules={[{ required: true }]} initialValue={"dashboard"}>
                    <Select>
                      <Select.Option value="dashboard">Dashboard (Homepage)</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="dateRange" label="Start & End Date" rules={[{ required: true }]}>
                    <RangePicker />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item 
                    name="doNotShowDays" 
                    label={
                      <span>
                        Do Not Show Days (number){` `}
                        <Tooltip title="How many days the Announcement will be hidden AFTER user seen it">
                          <InfoCircleOutlined />
                        </Tooltip>
                      </span>
                    }
                  >
                    
                    <InputNumber min={0} style={{ width: "100%" }} placeholder="Default: 7" />
                  </Form.Item>
                </Col>
              </Row>

              <Tabs defaultActiveKey="en" type="card">
                <TabPane tab="English" key="en">
                  <Form.Item name="title_en" label="Label (EN)" rules={[{ required: true }]}>
                    <Input placeholder="This is only for your reference. For title inside announcement, use Heading in Editor" />
                  </Form.Item>
                  <Form.Item name="text_en" label="Content (EN)" rules={[{ required: true }]}>
                    <ReactQuill
                      ref={quillRef}
                      theme="snow"
                      modules={quillModules}
                      formats={quillFormats}
                      style={{ height: "300px", marginBottom: "50px" }}
                    />
                  </Form.Item>
                </TabPane>
                <TabPane tab="Malay" key="ms">
                  <Form.Item name="title_ms" label="Label (MS)" rules={[{ required: true }]}>
                    <Input  placeholder="This is only for your reference. For title inside announcement, use Heading in Editor" />
                  </Form.Item>
                  <Form.Item name="text_ms" label="Content (MS)"  rules={[{ required: true }]}>
                    <ReactQuill
                      ref={quillRef}
                      theme="snow"
                      modules={quillModules}
                      formats={quillFormats}
                      style={{ height: "300px", marginBottom: "50px" }}
                    />
                  </Form.Item>
                </TabPane>
                <TabPane tab="Chinese" key="zh">
                  <Form.Item name="title_zh" label="Label (ZH)" rules={[{ required: true }]}>
                    <Input  placeholder="This is only for your reference. For title inside announcement, use Heading in Editor" />
                  </Form.Item>
                  <Form.Item name="text_zh" label="Content (ZH)" rules={[{ required: true }]}>
                    <ReactQuill
                      ref={quillRef}
                      theme="snow"
                      modules={quillModules}
                      formats={quillFormats}
                      style={{ height: "300px", marginBottom: "50px" }}
                    />
                  </Form.Item>
                </TabPane>
              </Tabs>

              <Button type="primary" htmlType="submit" loading={loading.create} className="mr-2">
                Create
              </Button>
              <em>To test on Announcement, open Partner with incognito mode so that "Do Not Show Days" is not accounted</em>
            </Form>
          </Collapse.Panel>
        </Collapse>
      )}

      <Collapse
        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
        style={{ marginTop: 16 }}
      >
        <Collapse.Panel header="Filters" key="filters">
          <AnnouncementFilters filters={filters} onFiltersChange={setFilters} />
        </Collapse.Panel>
      </Collapse>

      <Row justify="end">
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination?.limit}
            onChange={((currentPage, limit) => { setPagination({ ...pagination, limit, currentPage }) })}
            current={pagination.currentPage}
          />
        </Col>
      </Row>

      <Table
        rowKey="objectId"
        columns={columns}
        dataSource={announcements}
        pagination={false}
        loading={loading.index || loading.update}
        scroll={{ x: 1200 }}
        style={{ marginTop: 16 }}
      />
    </div>
  );
};

export default Announcement;
